/**
 * Whether a phone number has the length InGo's server reads (armkit/phone,
 * against the home market): the refusal it actually gives, measured on live
 * (5 Oct 2026, `D:/ingo0d-tools/probe-live-phone-rule.mjs`). Every usual way
 * of writing a right number passed there ("0772 123 456", "772123456",
 * "+256 0772 123456", Zimbabwe's "+263…"); only a digit missing or one too
 * many was refused ("0772 12345", "07721234567"). Rio's booking failed so,
 * at the last step, without saying whose number.
 *
 * The length is taken from each country's own example in Settings, Regional
 * ("772 123456": nine digits after the 0). A number for a country InGo does
 * not operate in is only kept within 8 to 15 digits; the server judges it.
 * Empty is fine here: the fields that use this are optional.
 *
 * @param {string} raw
 * @param {{ home: { name: string, dialCode: string, phoneExample: string }, countries?: { dialCode: string, phoneExample: string }[] } | null} region
 * @returns {{ ok: true } | { ok: false, message: string }}
 */
export function phoneShape(raw, region) {
  const text = String(raw ?? '').trim();
  if (!text) return { ok: true };
  const digits = text.replace(/\D/g, '');
  const home = region?.home ?? null;
  const known = home ? [home, ...(region.countries ?? [])] : [];
  const lengthOf = (country) => String(country?.phoneExample ?? '').replace(/\D/g, '').length;
  const codeOf = (country) => String(country?.dialCode ?? '').replace(/\D/g, '');

  let ok;
  if (text.startsWith('+')) {
    const country = known.find((c) => codeOf(c) && lengthOf(c) && digits.startsWith(codeOf(c)));
    ok = country
      ? digits.slice(codeOf(country).length).replace(/^0/, '').length === lengthOf(country)
      : digits.length >= 8 && digits.length <= 15;
  } else if (home && lengthOf(home) && codeOf(home)) {
    const n = lengthOf(home);
    ok = digits.replace(/^0/, '').length === n || (digits.startsWith(codeOf(home)) && digits.length === codeOf(home).length + n);
  } else {
    ok = digits.length >= 7 && digits.length <= 15;
  }
  if (ok) return { ok: true };

  if (home && lengthOf(home)) {
    return {
      ok: false,
      message: `Check the number: in ${home.name} it is ${lengthOf(home) + 1} digits starting with 0, like 0${home.phoneExample}, or + and the country code.`,
    };
  }

  return { ok: false, message: 'Check the number: a digit seems to be missing or one too many.' };
}
